import { useState, useCallback, useEffect, useMemo } from "react";
import Button from "../../Button/Button";
import CheckIcon from "../../../assets/svg/check-icon";
import SearchIcon from "../../../assets/svg/search-icon";
import VerbachatIcon from "../../../assets/svg/verbachat-icon";
import { useGetBotVersionsQuery } from "../../../redux/services/communicationApi";
import {
  selectFilterState,
  setCommunicationsBotVersion,
} from "../../../redux/slices/filterSlice";
import { useDispatch, useSelector } from "react-redux";

const ChatbotVersionFilter = ({ onClose }: { onClose: () => void }) => {
  const dispatch = useDispatch();
  const filterState = useSelector(selectFilterState);
  const currentBotVersion = filterState?.communications?.botVersion;

  const [searchQuery, setSearchQuery] = useState("");
  const [selectedIds, setSelectedIds] = useState<string[]>(
    (currentBotVersion?.value as string[]) ?? []
  );

  const { data: botVersions, isLoading } = useGetBotVersionsQuery();

  const versions = useMemo(
    () =>
      botVersions?.map((v) => ({
        value: v.version,
        label: v.name,
      })) ?? [],
    [botVersions]
  );

  const [filteredVersions, setFilteredVersions] = useState(versions);

  const handleSearch = useCallback(
    (query: string) => {
      if (!query.trim()) {
        setFilteredVersions(versions);
        return;
      }
      const filtered = versions.filter((v) =>
        v.label.toLowerCase().includes(query.toLowerCase())
      );
      setFilteredVersions(filtered);
    },
    [versions]
  );

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      handleSearch(searchQuery);
    }, 300);
    return () => clearTimeout(timeoutId);
  }, [searchQuery, handleSearch]);

  useEffect(() => {
    setFilteredVersions(versions);
  }, [versions]);

  const handleToggle = (versionId: string) => {
    setSelectedIds((prev) =>
      prev.includes(versionId)
        ? prev.filter((id) => id !== versionId)
        : [...prev, versionId]
    );
  };

  const handleApply = () => {
    const selectedDisplayValues = selectedIds
      .map((id) => versions.find((v) => v.value === id)?.label)
      .filter(Boolean) as string[];

    dispatch(
      setCommunicationsBotVersion({
        value: selectedIds,
        displayValue: selectedDisplayValues,
      })
    );
    onClose();
  };

  const isApplyDisabled =
    JSON.stringify([...selectedIds].sort()) ===
    JSON.stringify([...((currentBotVersion?.value as string[]) ?? [])].sort());

  return (
    <div className="flex flex-col h-full max-h-[calc(100vh-70px)]">
      <div className="relative border-b border-border-gray dark:border-border-gray-dk">
        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
          <SearchIcon className="h-4 w-4 text-gray-placeholder dark:text-gray-placeholder-dk" />
        </div>
        <input
          type="text"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          placeholder="Search versions"
          className="w-full pl-10 pr-4 py-2 text-sm outline-none text-heading-text dark:text-heading-text-dk placeholder:text-gray-placeholder dark:placeholder:text-gray-placeholder-dk bg-transparent"
        />
      </div>

      <div className="overflow-y-auto">
        {selectedIds.length > 0 && (
          <div className="flex flex-col divide-y divide-border-gray dark:divide-border-gray-dk">
            {selectedIds.map((id) => (
              <div
                key={id}
                onClick={() => handleToggle(id)}
                className="flex items-center justify-between p-3 bg-verba-blue-light-card-bg dark:bg-verba-blue-light-card-bg-dk cursor-pointer hover:bg-hover-gray dark:hover:bg-hover-gray-dk"
              >
                <div className="flex items-center gap-2">
                  <VerbachatIcon className="w-[18px] h-[18px] text-verba-blue dark:text-verba-blue-dk" />
                  <span className="text-sm font-medium text-heading-text dark:text-heading-text-dk">
                    {versions.find((v) => v.value === id)?.label ?? id}
                  </span>
                </div>
                <div className="flex items-center justify-center w-3 h-3 border rounded border-verba-blue dark:border-verba-blue-dk bg-verba-blue dark:bg-verba-blue-dk">
                  <CheckIcon className="w-3 h-3 text-pure-white dark:text-pure-white-dk" />
                </div>
              </div>
            ))}
          </div>
        )}

        <div className="flex flex-col">
          <div className="px-3 py-2 text-xs font-semibold text-dark-gray dark:text-dark-gray-dk uppercase border-b border-border-gray dark:border-border-gray-dk">
            All Versions
          </div>
          <div className="overflow-y-auto">
            {isLoading ? (
              <div className="flex items-center justify-center py-8">
                <div className="text-sm text-gray-placeholder dark:text-gray-placeholder-dk">
                  Loading versions...
                </div>
              </div>
            ) : filteredVersions.length === 0 ? (
              <div className="flex items-center justify-center py-8">
                <div className="text-sm text-gray-placeholder dark:text-gray-placeholder-dk">
                  {searchQuery.trim()
                    ? "No versions found"
                    : "No versions available"}
                </div>
              </div>
            ) : (
              filteredVersions
                .filter((v) => !selectedIds.includes(v.value))
                .map((version) => (
                  <div
                    key={version.value}
                    onClick={() => handleToggle(version.value)}
                    className="flex items-center justify-between p-3 cursor-pointer hover:bg-hover-gray dark:hover:bg-hover-gray-dk border-b border-border-gray dark:border-border-gray-dk last:border-b-0"
                  >
                    <div className="flex items-center gap-2">
                      <VerbachatIcon className="w-[18px] h-[18px] text-verba-blue dark:text-verba-blue-dk" />
                      <span className="text-md text-heading-text dark:text-heading-text-dk">
                        {version.label}
                      </span>
                    </div>
                    <div className="flex items-center justify-center w-3 h-3 border rounded border-border-gray dark:border-border-gray-dk">
                      <div className="w-3 h-3 rounded-sm bg-transparent" />
                    </div>
                  </div>
                ))
            )}
          </div>
        </div>
      </div>

      <div className="flex justify-end gap-2 mt-6 px-3">
        <Button
          innerText="Cancel"
          buttonContainerClass="py-1 px-7.5 border border-verba-indigo dark:border-verba-indigo-dk rounded-lg"
          innerTextClass="text-md font-medium whitespace-nowrap text-heading-text dark:text-heading-text-dk"
          onClick={onClose}
        />
        <Button
          innerText="Apply"
          disabled={isApplyDisabled}
          buttonContainerClass="py-1 px-7.5 rounded-lg bg-verba-indigo dark:bg-verba-indigo-dk disabled:opacity-50 disabled:cursor-not-allowed"
          innerTextClass="text-md font-medium whitespace-nowrap text-pure-white dark:text-pure-white-dk"
          onClick={handleApply}
        />
      </div>
    </div>
  );
};

export default ChatbotVersionFilter;
